import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInterview } from '../../utils/interviewContext';
import { trackPostHogEvent } from '../../utils/posthogUtils';
import { getInterviewFlow, nextStep, previousStep, stepPosition } from '../../utils/interviewFlow';
import { resultPath } from '../../utils/interviewRecommendation';
import styles from './interviewQuestion.module.css';
import { Collapse } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import HistoryIcon from '@mui/icons-material/History';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ProgressBar from '../.shared/ProgressBar/ProgressBar';
import Footer from '../.shared/footer/footer';
import Header from '../.shared/header/header';

const PATH = '/interview/glp1-history';

const glp1Options = [
    { value: 'never', label: 'No, never', Icon: BlockIcon },
    { value: 'currently', label: "Yes, I'm taking one now", Icon: LocalPharmacyIcon },
    { value: 'past', label: "Yes, but I've stopped", Icon: HistoryIcon },
    { value: 'unsure', label: "I'm not sure", Icon: HelpOutlineIcon }
];

export default function InterviewGlp1History() {
    const navigate = useNavigate();

    const { updateInterviewData, interviewData } = useInterview();
    const [ selectedHistory, setSelectedHistory ] = useState('none');

    // Read once per mount, so the chrome and the navigation cannot disagree if
    // the stored assignment changes while the page is open.
    const flow = useMemo(getInterviewFlow, []);

    // Populate from localStorage on component mount
    useEffect(() => {
        if (interviewData.glp1Experience) {
            setSelectedHistory(interviewData.glp1Experience);
        }

        // Track when user reaches the GLP-1 history step
        trackPostHogEvent('interview_step_viewed', {
            interview_step: 'glp1-history',
            step_number: stepPosition(flow, PATH),
            total_steps: flow.steps.length,
            interview_flow: flow.id,
            has_existing_data: !!interviewData.glp1Experience
        });
    }, [interviewData.glp1Experience, flow]);

    const handleConfirm = (recentlyChosen) => {
        if (selectedHistory === 'none' && !recentlyChosen) return;

        const chosenHistory = recentlyChosen ? recentlyChosen : selectedHistory;

        setSelectedHistory(chosenHistory);

        // Timeout to allow button selection animation to complete
        setTimeout(() => {
            // Save to interview context
            updateInterviewData('glp1Experience', chosenHistory);

            // Track GLP-1 history selection
            trackPostHogEvent('interview_glp1_selected', {
                selected_glp1_history: chosenHistory,
                glp1_label: glp1Options.find((option) => option.value === chosenHistory)?.label,
                interview_step: 'glp1-history',
                step_number: stepPosition(flow, PATH),
                interview_flow: flow.id
            });

            // Last step of the extended funnel. The recommendation was derived
            // back on the priority step and lives in the interview context, so
            // /result still receives the query params it renders from.
            navigate(nextStep(flow, PATH) ?? resultPath(interviewData));
        }, 300);
    };

    const handleBack = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate(previousStep(flow, PATH) ?? '/');
    };

    return (
        <>
            <div className={styles.interviewPageContainer}>
                <Header hideNavigation={flow.hideNavigation} />
                <ProgressBar step={stepPosition(flow, PATH)} totalSteps={flow.steps.length} />
                <Collapse
                    in
                    appear
                    timeout={500}
                    unmountOnExit
                    mountOnEnter
                    orientation="vertical"
                >
                    <div className={styles.questionContent}>
                        <h1 className={styles.title}>Have you taken a GLP-1 medication before?</h1>
                        <div className={styles.buttonsContainer}>

                            {glp1Options.map(({ value, label, Icon }) => (
                                <button
                                    key={value}
                                    className={`${styles.interviewButton} ${selectedHistory === value ? styles.selected : ''}`}
                                    onClick={() => handleConfirm(value)}
                                >
                                    <Icon sx={{ color: `${selectedHistory === value ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                    <span>{label}</span>
                                </button>
                            ))}

                            <div className={styles.confirmationButtons}>
                                <button
                                    className={styles.secondaryButton}
                                    onClick={handleBack}
                                >
                                    Back
                                </button>
                                <button
                                    className={`${styles.primaryButton} ${selectedHistory === 'none' ? styles.disabledButton : ''}`}
                                    onClick={() => handleConfirm()}
                                >
                                    See My Results
                                </button>
                            </div>

                        </div>
                    </div>
                </Collapse>
            </div>
            <Footer />
        </>
    );
}
